import {useEffect, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {readEndpoint} from 'redux-json-api'

// The API marks an upload encrypted just before it generates the previews,
// so a photo that has only just landed can have no cover yet.
const COVER_RETRY_DELAY_MS = 1200
const COVER_RETRY_LIMIT = 5

/** The full stored copy of a story photo (2048px), as a data URL. */
export const loadFullStoryPhoto = (dispatch, uploadId) =>
  dispatch(readEndpoint(`uploads/${uploadId}?download=true`)).then(response => {
    const attributes = response.body.data.attributes

    return `data:${attributes.filetype};base64,${attributes['decrypted-file']}`
  })

/**
 * The 800px cover of each story photo (TK-3108), as data URLs keyed by upload
 * id. Covers come decrypted from `upload-previews/:id` (always JPEG) and are
 * fetched once per id. `images[id]` is undefined while loading and null when
 * the photo can't be shown.
 */
const useStoryPhotoImages = uploadIds => {
  const dispatch = useDispatch()
  const [images, setImages] = useState({})
  const requestedRef = useRef(new Set())
  const mountedRef = useRef(true)

  useEffect(() => () => {
    mountedRef.current = false
  }, [])

  const idsKey = uploadIds.join(',')

  useEffect(() => {
    const settle = (uploadId, src) => {
      if (mountedRef.current) setImages(current => ({...current, [uploadId]: src}))
    }

    const fetchCover = (uploadId, attempt = 1) => {
      dispatch(readEndpoint(`upload-previews/${uploadId}`))
        .then(response => {
          const cover = response.body.data.attributes.cover

          if (cover) settle(uploadId, `data:image/jpeg;base64,${cover}`)
          else if (attempt < COVER_RETRY_LIMIT && mountedRef.current) setTimeout(() => fetchCover(uploadId, attempt + 1), COVER_RETRY_DELAY_MS)
          else settle(uploadId, null)
        })
        .catch(() => settle(uploadId, null))
    }

    uploadIds.forEach(uploadId => {
      if (requestedRef.current.has(uploadId)) return

      requestedRef.current.add(uploadId)
      fetchCover(uploadId)
    })
  }, [dispatch, idsKey])

  return images
}

/**
 * The full stored copy of each story photo (TK-3108, Preview), as data URLs
 * keyed by upload id, fetched once per id and only while `enabled` (Preview
 * turns it on as a story's photos near the screen). `photos[id]` is undefined
 * while loading and null when the photo can't be shown.
 */
export const useFullStoryPhotos = (uploadIds, {enabled = true} = {}) => {
  const dispatch = useDispatch()
  const [photos, setPhotos] = useState({})
  const requestedRef = useRef(new Set())
  const mountedRef = useRef(true)

  useEffect(() => () => {
    mountedRef.current = false
  }, [])

  const idsKey = uploadIds.join(',')

  useEffect(() => {
    if (!enabled) return

    uploadIds.forEach(uploadId => {
      if (requestedRef.current.has(uploadId)) return

      requestedRef.current.add(uploadId)
      loadFullStoryPhoto(dispatch, uploadId)
        .then(src => { if (mountedRef.current) setPhotos(current => ({...current, [uploadId]: src})) })
        .catch(() => { if (mountedRef.current) setPhotos(current => ({...current, [uploadId]: null})) })
    })
  }, [dispatch, enabled, idsKey])

  return photos
}

export default useStoryPhotoImages
